import React, { useEffect, useRef, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, ArrowLeft, X } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { fastIpfsUrl } from "../lib/media";
import CheckoutDialog from "./CheckoutDialog";
import IpfsImage from "./IpfsImage";
import { LegalDisclaimer } from "./LegalDisclaimer";

const HISTORY_KEY = "cart-open";

const CartSheet = () => {
    const { items, total, open, setOpen, updateQty, removeItem, count } = useCart();
    const [checkoutOpen, setCheckoutOpen] = useState(false);
    const pushedRef = useRef(false);

    useEffect(() => {
        if (!open) return;
        const state = window.history.state;
        if (!state || state[HISTORY_KEY] !== true) {
            window.history.pushState({ [HISTORY_KEY]: true }, "");
            pushedRef.current = true;
        }
        const onPop = () => {
            pushedRef.current = false;
            setOpen(false);
        };
        window.addEventListener("popstate", onPop);
        return () => {
            window.removeEventListener("popstate", onPop);
            if (pushedRef.current) {
                pushedRef.current = false;
                const cur = window.history.state;
                if (cur && cur[HISTORY_KEY] === true) {
                    window.history.back();
                }
            }
        };
    }, [open, setOpen]);

    const handleClose = () => setOpen(false);

    return (
        <>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent
                    side="right"
                    className="w-full sm:max-w-md bg-[#0a0610] border-l border-[#7c3aed]/40 flex flex-col p-0 text-white [&>button]:hidden"
                    data-testid="cart-sheet"
                >
                    <SheetHeader className="px-4 pt-5 pb-4 border-b border-[#7c3aed]/30 bg-[#150826]/85 backdrop-blur">
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleClose}
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#150826]/70 hover:bg-[#1f0f36] border border-[#7c3aed]/50 hover:border-[#e879f9] text-[#e879f9] text-xs font-mono uppercase tracking-[0.18em] font-semibold active:scale-95 transition-all"
                                data-testid="cart-back-btn"
                                aria-label="Torna al menu"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                <span className="hidden sm:inline">Continua</span>
                            </button>
                            <SheetTitle className="flex-1 font-serif-display text-2xl sm:text-3xl flex items-center gap-2 text-white text-left">
                                <ShoppingBag className="w-5 h-5 text-[#e879f9]" /> Carrello
                                {count > 0 && (
                                    <span className="font-mono text-sm text-[#e879f9]">({count})</span>
                                )}
                            </SheetTitle>
                            <button
                                onClick={handleClose}
                                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#150826]/70 hover:bg-[#e74c5e] hover:text-white border border-[#7c3aed]/50 hover:border-[#e74c5e] text-[#e879f9] active:scale-95 transition-all"
                                data-testid="cart-close-btn"
                                aria-label="Chiudi carrello"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                    </SheetHeader>

                    <div className="flex-1 overflow-y-auto px-5 py-4">
                        {items.length === 0 ? (
                            <div className="text-center py-20" data-testid="cart-empty">
                                <ShoppingBag className="w-12 h-12 mx-auto mb-4 text-[#e879f9] opacity-60" />
                                <p className="text-white font-serif-display text-xl">Il carrello è vuoto.</p>
                                <p className="text-sm mt-2 text-white/55 font-mono">Aggiungi qualche prodotto dal menu.</p>
                                <button
                                    onClick={handleClose}
                                    className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#7c3aed] to-[#9333ea] hover:from-[#a78bfa] hover:to-[#e879f9] text-white font-mono uppercase tracking-[0.15em] text-xs font-bold shadow-[0_4px_14px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_22px_rgba(232,121,249,0.7)] active:scale-95 transition-all"
                                    data-testid="cart-empty-back-btn"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Torna al menu
                                </button>
                            </div>
                        ) : (
                            <ul className="space-y-3">
                                {items.map((item) => (
                                    <li
                                        key={`${item.product_id}-${item.variant_label || "default"}`}
                                        className="flex gap-3 bg-[#150826]/70 backdrop-blur rounded-2xl border border-[#7c3aed]/30 hover:border-[#e879f9]/60 transition-colors p-3"
                                        data-testid={`cart-item-${item.product_id}-${item.variant_label || "default"}`}
                                    >
                                        {item.photo_url && (
                                            <IpfsImage
                                                src={fastIpfsUrl(item.photo_url)}
                                                alt={item.product_name}
                                                loading="lazy"
                                                decoding="async"
                                                className="w-20 h-20 object-cover rounded-xl border border-[#7c3aed]/40"
                                            />
                                        )}
                                        <div className="flex-1 min-w-0">
                                            <div className="font-serif-display text-base leading-tight text-white truncate">
                                                {item.product_name}
                                            </div>
                                            <div className="font-mono text-xs text-[#e879f9] mt-0.5 font-semibold">
                                                €{item.price.toFixed(2)} / {item.unit}
                                            </div>
                                            <div className="flex items-center gap-2 mt-2">
                                                <button
                                                    onClick={() => updateQty(item.product_id, item.quantity - 1, item.variant_label)}
                                                    className="w-7 h-7 inline-flex items-center justify-center bg-[#0d0418] border border-[#7c3aed]/50 hover:border-[#e879f9] rounded-full text-[#e879f9]"
                                                    data-testid={`decrement-${item.product_id}`}
                                                >
                                                    <Minus className="w-3 h-3" />
                                                </button>
                                                <span className="font-mono text-sm w-6 text-center text-white tabular-nums">{item.quantity}</span>
                                                <button
                                                    onClick={() => updateQty(item.product_id, item.quantity + 1, item.variant_label)}
                                                    className="w-7 h-7 inline-flex items-center justify-center bg-[#0d0418] border border-[#7c3aed]/50 hover:border-[#e879f9] rounded-full text-[#e879f9]"
                                                    data-testid={`increment-${item.product_id}`}
                                                >
                                                    <Plus className="w-3 h-3" />
                                                </button>
                                                <button
                                                    onClick={() => removeItem(item.product_id, item.variant_label)}
                                                    className="ml-auto text-[#e74c5e]/80 hover:text-[#ff6b7a]"
                                                    data-testid={`remove-${item.product_id}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </div>
                                        </div>
                                        <div className="font-mono text-sm text-[#e879f9] font-bold tabular-nums">
                                            €{(item.price * item.quantity).toFixed(2)}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {items.length > 0 && (
                        <div className="border-t border-[#7c3aed]/30 p-5 bg-[#150826]/80 backdrop-blur">
                            <div className="mb-3 flex justify-center">
                                <LegalDisclaimer variant="compact" />
                            </div>
                            <div className="flex items-center justify-between mb-4">
                                <span className="font-mono text-xs uppercase tracking-widest text-white/55">
                                    Totale
                                </span>
                                <span className="font-mono text-2xl text-[#e879f9] font-bold drop-shadow-[0_0_8px_rgba(232,121,249,0.45)] tabular-nums" data-testid="cart-total">
                                    €{total.toFixed(2)}
                                </span>
                            </div>
                            <button
                                onClick={() => setCheckoutOpen(true)}
                                className="w-full py-3 bg-gradient-to-r from-[#7c3aed] to-[#9333ea] hover:from-[#a78bfa] hover:to-[#e879f9] text-white font-mono uppercase tracking-[0.15em] text-xs font-bold rounded-full shadow-[0_4px_18px_rgba(124,58,237,0.55)] hover:shadow-[0_6px_28px_rgba(232,121,249,0.75)] active:scale-[0.98] transition-all"
                                data-testid="checkout-btn"
                            >
                                Procedi al Checkout
                            </button>
                        </div>
                    )}
                </SheetContent>
            </Sheet>

            <CheckoutDialog
                open={checkoutOpen}
                onClose={() => setCheckoutOpen(false)}
                onSuccess={() => {
                    setCheckoutOpen(false);
                    setOpen(false);
                }}
            />
        </>
    );
};

export default CartSheet;
